import {Component, EventEmitter, Input, Output, TemplateRef} from '@angular/core';
import {PodState, PodValidation, PodValidationDeliveryType} from '../shared/pod-validation.model';
import {AuthenticationService} from '../../shared/services/authentication.service';
import {PodValidationService} from '../shared/pod-validation.service';
import {LoaderService} from '../../shared/services/loader.service';
import {ToastrService} from 'ngx-toastr';
import {finalize, switchMap, tap} from 'rxjs/operators';
import {ErrorHandlerService} from '../../shared/services/error-handler.service';
import {TranslateService} from '@ngx-translate/core';
import {ModalService} from '../../shared/services/modal.service';
import { NavigationLinkService } from '../../shared/services/navigation-link.service';

@Component({
  selector: 'app-pods-validation-list-item',
  templateUrl: './pods-validation-list-item.component.html',
  styleUrls: ['./pods-validation-list-item.component.scss']
})
export class PodsValidationListItemComponent {

  @Input() public podValidation: PodValidation;
  @Input() public displayedPod: PodValidation;
  @Output() public onPodClicked: EventEmitter<void> = new EventEmitter<void>();
  @Output() public onPodValidated: EventEmitter<void> = new EventEmitter<void>();
  @Output() public onDocumentUploaded: EventEmitter<void> = new EventEmitter<void>();
  @Output() public onPodRejected: EventEmitter<void> = new EventEmitter<void>();
  @Output() public appendOriginGlidFilter: EventEmitter<string> = new EventEmitter<string>();
  @Output() public appendDestinationGlidFilter: EventEmitter<string> = new EventEmitter<string>();

  public rejectReason: string;
  public isPodManager = false;
  public isPodViewer = false;
  public isWorking = false;
  public fileToUpload: File;
  public acceptedFileTypes = PodValidationService.getPodValidationAcceptedTypes();
  public acceptedFileExtensions = ['.png', '.jpeg', '.pdf'];
  public PodValidationTypes = PodValidationDeliveryType;

  constructor(
    private authService: AuthenticationService,
    private podValidationService: PodValidationService,
    private loaderService: LoaderService,
    private toastrService: ToastrService,
    private modalService: ModalService,
    private translateService: TranslateService,
    private errorHandlerService: ErrorHandlerService,
    public link: NavigationLinkService,
  ) {
    this.isPodViewer = this.authService.canAccessPodsValidation();
    this.isPodManager = this.authService.canManagePodsValidation();
  }

  public rejectPod(podValidation: PodValidation, content: TemplateRef<any>): void {
    this.rejectReason = null;
    this.modalService.open({
      title: this.translateService.instant('pods-validation.reject.title'),
      content,
      closeButton: {
        text: this.translateService.instant('pods-validation.reject.cancel'),
        style: { 'text-transform': 'none' }
      },
      validateButton: {
        text: this.translateService.instant('pods-validation.reject.validate'),
        icon: 'fas fa-envelope',
        style: { 'text-transform': 'none' }
      },
    }).pipe(
      tap(() => {
        this.isWorking = true;
        this.loaderService.startLoad();
      }),
      switchMap(() => this.podValidationService.reject(podValidation, this.rejectReason)),
      finalize(() => {
        this.isWorking = false;
        this.loaderService.stopLoad();
      })
    ).subscribe(() => {
      this.onPodRejected.emit();
      this.toastrService.success(
        this.translateService.instant(
          'pods-validation.reject.success',
          {loadNumber: podValidation.loadNumber, deliveryNumber: podValidation.deliveryNumber}
        )
      );
    }, e => this.errorHandlerService.rethrowHttpError(e, 'pods-validation.reject.error'));
  }

  public validatePod(podValidation: PodValidation): void {
    this.loaderService.startLoad();
    this.isWorking = true;
    this.podValidationService.validate(podValidation).pipe(
      finalize(() => {
        this.isWorking = false;
        this.loaderService.stopLoad();
      })
    ).subscribe(() => {
      this.onPodValidated.emit();
      this.toastrService.success(
        this.translateService.instant(
          'pods-validation.validate.success',
          {loadNumber: podValidation.loadNumber, deliveryNumber: podValidation.deliveryNumber}
        )
      );
    }, e => this.errorHandlerService.handleHttpError(e, 'pods-validation.validate.error'));
  }

  public displayPodManagerActions(): boolean {
    return this.isPodManager
      && this.displayedPod
      && this.displayedPod.id === this.podValidation.id
      && this.podValidation.state === PodState.CHEP_REVIEW;
  }

  public displayUploadActions(): boolean {
    return this.podValidation.state === PodState.PENDING;
  }

  public displaySeeButton(): boolean {
    return this.podValidation.documentId != null
      && (this.displayedPod == null || this.displayedPod.id !== this.podValidation.id);
  }

  public uploadPodFile(file: File, mrFinalQuantityModal: TemplateRef<any>): void {
    if (this.podValidation.type === PodValidationDeliveryType.MR) {
      this.modalService.open({
        content: mrFinalQuantityModal,
        title: this.translateService.instant('pods-validation.confirmation-window.shipped-quantities-title'),
        size: 'sm',
        validateButton: {
          disabled: () => this.podValidation.mrFinalQuantity == null,
          text: this.translateService.instant('pods-validation.tabs.item.actions.confirm'),
          icon: 'fa fa-check'
        }
      }).subscribe(() => {
        this.upload(file);
      })
    } else {
      this.upload(file);
    }
  }

  private isDeliveryItemsInputDisabled() {
    return !this.podValidation.deliveryItems ||
            this.podValidation.deliveryItems.length === 0 ||
            this.podValidation.deliveryItems.filter(i => !i.productDescription || (!i.pieces && i.pieces !== 0)).length > 0;
  }

  private upload(file: File) {
    this.loaderService.startLoad();
    this.isWorking = true;
    this.podValidationService.upload(this.podValidation, file).pipe(
      finalize(() => {
        this.isWorking = false;
        this.loaderService.stopLoad();
      })
    ).subscribe(() => {
      this.onDocumentUploaded.emit();
      this.toastrService.success(
        this.translateService.instant(
          'pods-validation.upload.success',
          {loadNumber: this.podValidation.loadNumber, deliveryNumber: this.podValidation.deliveryNumber}
        )
      );
    }, e => this.errorHandlerService.handleHttpError(e, 'pods-validation.upload.error'));
  }

  appendOriginGlidInFilter(originGlid: string) {
    this.appendOriginGlidFilter.emit(originGlid);
  }

  appendDestinationGlidInFilter(destinationGlid: string) {
    this.appendDestinationGlidFilter.emit(destinationGlid);
  }

  public onNumberInputKeydown(event: KeyboardEvent): void {
    const forbiddenKeys = ['e', '-'];
    const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Home', 'End'];

    if (
      forbiddenKeys.includes(event.key) ||
      (!allowedKeys.includes(event.key) && isNaN(parseInt(event.key, 10)))
    ) {
      event.preventDefault();
    }
  }
}
